import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ExternalLink,
  Plus, Trash2, ChevronRight, ChevronUp, ChevronDown, Check,
  Loader2, Edit3, Eye, EyeOff, RotateCcw, Mail
} from 'lucide-react';
import { PageWrapper } from '../components/PageWrapper';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  getSettings, saveSettings,
  isPasscodeEnabled, setPasscodeEnabled, savePasscode, clearPasscode,
  getNotificationSettings, saveNotificationSettings,
  isAICompanionEnabled, setAICompanionEnabled,
  getEnabledGoals, addGoal, updateGoal, deleteGoal, reorderGoals,
  getDefaultPrompts, addPrompt, updatePrompt, deletePrompt, resetPromptToDefault, restoreAllDefaultPrompts, reorderPrompts,
  clearAIWelcomeCache,
  saveTextScale,
  getTextScale,
  TEXT_SCALES,
  SECURITY_TYPE,
  getSecurityType,
  setSecurityType,
} from '@/utils/storage';
import {
  isNotificationSupported, requestNotificationPermission,
  scheduleNotification, cancelNotification,
} from '../utils/notifications';
import { isAppleIntelligenceSupported, isNativeiOS } from '../utils/aiCompanion';
import { useTheme } from '../components/ThemeProvider';
import { toast } from 'sonner';
import { isBiometricsAvailable, authenticateWithBiometrics } from '@/utils/biometricAuth';


// Capitalize first letter
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const getContrastTextColor = (hex) => {
  if (!hex || typeof hex !== 'string') return '#2D2D35';
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#2D2D35';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#2D2D35' : '#F9F9FF';
};

export const Settings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(getSettings() || { name: '', goals: [], prompts: getDefaultPrompts() });
  const refreshSettings = () => setSettings(getSettings());

  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  const { theme, changeTheme, themes } = useTheme();
  const [textScale, setTextScale] = useState(getTextScale());

  // Passcode dialog state
  const [passcodeEnabled, setLocalPasscodeEnabled] = useState(isPasscodeEnabled());
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcodeValue, setPasscodeValue] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [passcodeStep, setPasscodeStep] = useState('enter');

  // NEW SECURITY STATE
  const [securityType, setSecurityTypeState] = useState(() => {
    return getSecurityType() || 'none';
  });
  const [biometricsAvailable, setBiometricsAvailable] = useState(false);

  // Notifications
  const [notifSettings, setNotifSettings] = useState(getNotificationSettings());
  const [notifSupported, setNotifSupported] = useState(true);
  
  // Goal dialog
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ text: '', easyText: '' });

  // Prompt dialog
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptForm, setPromptForm] = useState({ question: '' });

  // AI
  const [aiCompanionEnabled, setLocalAICompanionEnabled] = useState(isAICompanionEnabled());
  const [aiSupported, setAiSupported] = useState(false);

  // EFFECTS
  useEffect(() => {
    setNotifSupported(isNotificationSupported());
  }, []);

  useEffect(() => {
    const checkBiometrics = async () => {
      const available = await isBiometricsAvailable();
      setBiometricsAvailable(available);
    };
    checkBiometrics();
  }, []);

  useEffect(() => {
    const checkAI = async () => {
      const supported = await isAppleIntelligenceSupported();
      setAiSupported(supported);
    };
    checkAI();
  }, []);

  // SECURITY HANDLERS
  const handlePasscodeToggle = async (enabled) => {
    if (enabled) {
      setShowPasscodeDialog(true);
      setPasscodeStep('enter');
      setPasscodeValue('');
      setConfirmPasscode('');
    } else {
      clearPasscode();
      setSecurityType('none');
      setSecurityTypeState('none');
      toast.success('Passcode disabled');
    }
  };

  const handleBiometricsToggle = async (enabled) => {
    if (enabled) {
      if (!biometricsAvailable) {
        toast.error('Face ID / Touch ID is not available on this device');
        return;
      }
      
      const result = await authenticateWithBiometrics('Enable biometrics to unlock RE-BUILD-ME');
      if (result.success) {
        clearPasscode();
        setSecurityType('biometric');
        setSecurityTypeState('biometric');
        toast.success('Face ID / Touch ID enabled');
      } else {
        toast.error('Could not enable biometrics');
      }
    } else {
      setSecurityType('none');
      setSecurityTypeState('none');
      toast.success('Face ID / Touch ID disabled');
    }
  };

  // Passcode dialog submit
  const handlePasscodeSubmit = () => {
    if (passcodeStep === 'enter') {
      if (passcodeValue.length === 4) { 
        setPasscodeStep('confirm'); 
        setConfirmPasscode(''); 
      }
    } else {
      if (confirmPasscode === passcodeValue) {
        savePasscode(passcodeValue);
        setPasscodeEnabled(true);
        setLocalPasscodeEnabled(true);
        setShowPasscodeDialog(false);
        toast.success('Passcode enabled');
      } else {
        toast.error('Passcodes do not match');
        setPasscodeStep('enter');
        setPasscodeValue('');
        setConfirmPasscode('');
      }
    }
  };

  // AI Handler
  const handleAICompanionToggle = (enabled) => {
    setAICompanionEnabled(enabled);
    setLocalAICompanionEnabled(enabled);
    clearAIWelcomeCache();
    if (enabled && !aiSupported) {
      toast('AI Companion enabled. It will start once Apple Intelligence is available on this device.');
      return;
    }
    toast.success(enabled ? 'AI Companion enabled' : 'AI Companion disabled');
  };

  const handleTextScaleChange = (scaleKey) => {
    saveTextScale(scaleKey);
    setTextScale(scaleKey);
    toast.success(`Text size set to ${scaleKey === 'xlarge' ? 'Extra Large' : scaleKey.charAt(0).toUpperCase() + scaleKey.slice(1)}`);
  };

  // Name handler
  const handleNameSave = () => {
    if (tempName.trim()) {
      const s = { ...settings, name: tempName.trim() };
      saveSettings(s);
      refreshSettings();
      setEditingName(false);
      clearAIWelcomeCache();
      toast.success('Name updated');
    }
  };

  // Notifications handlers
  const handleNotificationToggle = async (enabled) => {
    if (enabled) {
      const granted = await requestNotificationPermission();
      if (granted) {
        saveNotificationSettings(true, notifSettings.time);
        setNotifSettings({ ...notifSettings, enabled: true });
        await scheduleNotification(notifSettings.time);
        toast.success('Daily reminder enabled');
      } else { toast.error('Notification permission denied'); }
    } else {
      saveNotificationSettings(false, notifSettings.time);
      setNotifSettings({ ...notifSettings, enabled: false });
      await cancelNotification();
      toast.success('Daily reminder disabled');
    }
  };
  
  const handleTimeChange = async (e) => {
    const time = e.target.value;
    setNotifSettings({ ...notifSettings, time });
    saveNotificationSettings(notifSettings.enabled, time);
    if (notifSettings.enabled) {
      await scheduleNotification(time);
      toast.success(`Reminder set for ${time}`);
    }
  };

  // Goals CRUD
  const handleAddGoal = () => {
    if ((settings.goals || []).length >= 5) { toast.error('Maximum 5 goals'); return; }
    setEditingGoal(null);
    setGoalForm({ text: '', easyText: '' });
    setShowGoalDialog(true);
  };
  
  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({ text: goal.text, easyText: goal.easyText || '' });
    setShowGoalDialog(true);
  };
  
  const handleSaveGoal = () => {
    if (!goalForm.text.trim()) { toast.error('Goal text is required'); return; }
    if (editingGoal) {
      updateGoal(editingGoal.id, { text: goalForm.text.trim(), easyText: goalForm.easyText.trim() });
    } else {
      addGoal({ text: goalForm.text.trim(), easyText: goalForm.easyText.trim() });
    }
    clearAIWelcomeCache();
    refreshSettings();
    setShowGoalDialog(false);
    toast.success(editingGoal ? 'Goal updated' : 'Goal added');
  };
  
  const handleDeleteGoal = (goalId) => {
    deleteGoal(goalId);
    refreshSettings();
    clearAIWelcomeCache();
    toast.success('Goal removed');
  };
  
  const handleToggleGoal = (goalId, enabled) => {
    updateGoal(goalId, { enabled });
    refreshSettings();
    clearAIWelcomeCache();
  };

  // Prompts CRUD
  const handleAddPrompt = () => {
    if ((settings.prompts || []).length >= 10) { toast.error('Maximum 10 reflections'); return; }
    setEditingPrompt(null);
    setPromptForm({ question: '' });
    setShowPromptDialog(true);
  };
  
  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setPromptForm({ question: prompt.question });
    setShowPromptDialog(true);
  };
  
  const handleSavePrompt = () => {
    if (!promptForm.question.trim()) { toast.error('Question text is required'); return; }
    if (promptForm.question.trim().length > 150) { toast.error('Max 150 characters'); return; }
    if (editingPrompt) {
      updatePrompt(editingPrompt.id, { question: promptForm.question.trim() });
    } else {
      addPrompt(promptForm.question.trim());
    }
    refreshSettings();
    setShowPromptDialog(false);
    toast.success(editingPrompt ? 'Reflection updated' : 'Reflection added');
  };
  
  const handleDeletePrompt = (promptId) => {
    deletePrompt(promptId);
    refreshSettings();
    toast.success('Reflection removed');
  };
  
  const handleTogglePrompt = (promptId, enabled) => {
    updatePrompt(promptId, { enabled });
    refreshSettings();
  };
  
  const handleResetPrompt = (promptId) => {
    resetPromptToDefault(promptId);
    refreshSettings();
    toast.success('Reset to original');
  };
  
  const handleRestoreAll = () => {
    restoreAllDefaultPrompts();
    refreshSettings();
    toast.success('Defaults restored');
  };

  // Reorder functions
  const moveGoal = (index, direction) => {
    const arr = [...(settings.goals || [])].sort((a, b) => a.order - b.order);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= arr.length) return;
    [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];
    reorderGoals(arr);
    refreshSettings();
  };
  
  const movePrompt = (index, direction) => {
    const arr = [...(settings.prompts || [])].sort((a, b) => a.order - b.order);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= arr.length) return;
    [arr[index], arr[targetIndex]] = [arr[targetIndex], arr[index]];
    reorderPrompts(arr);
    refreshSettings();
  };

  const sortedGoals = [...(settings.goals || [])].sort((a, b) => a.order - b.order);
  const sortedPrompts = [...(settings.prompts || [])].sort((a, b) => a.order - b.order);

  // RETURN STATEMENT - ALL JSX GOES HERE
  return (
    <PageWrapper className="bg-app">
      <div className="max-w-md mx-auto px-6 py-8">
        {/* User Name */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <h2 className="font-body text-primary text-center mb-4" style={{ fontWeight: 600 }}>Name:</h2>
          {editingName ? (
            <div className="flex gap-2">
              <Input value={tempName} onChange={(e) => setTempName(e.target.value)} className="flex-1 bg-theme-light border-theme-secondary rounded-xl" placeholder="Enter your name" data-testid="name-input" autoFocus />
              <Button onClick={handleNameSave} className="bg-theme-primary text-primary hover:bg-theme-hover rounded-xl" data-testid="save-name-btn">Save</Button>
            </div>
          ) : (
            <button onClick={() => { setTempName(settings.name); setEditingName(true); }} className="relative w-full p-4 rounded-xl bg-theme-light text-center flex items-center justify-center hover:bg-theme-secondary transition-colors" data-testid="edit-name-btn">
              <span className="font-body text-primary text-center">{settings.name || 'Not set'}</span>
              <ChevronRight size={18} className="absolute right-4" style={{ color: 'var(--theme-accent, #A58CD6)' }} />
            </button>
          )}
        </motion.div>

        {/* Goals */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-body text-primary text-center flex-1" style={{ fontWeight: 600 }}>Goals:</h2>
            {(settings.goals || []).length < 5 && (
              <button onClick={handleAddGoal} className="p-2 rounded-full hover:bg-theme-light transition-colors" data-testid="add-goal-btn">
                <Plus size={20} style={{ color: 'var(--theme-accent, #8E6ECC)' }} />
              </button>
            )}
          </div>

          {sortedGoals.length === 0 ? (
            <p className="font-body text-sm text-secondary text-center py-4">You have no goals yet. Add one above to track something specific.</p>
          ) : (
            <div className="space-y-2">
              {sortedGoals.map((goal, index) => (
                <div key={goal.id} className="p-4 rounded-xl bg-theme-light flex items-center gap-2" data-testid={`goal-item-${goal.id}`}>
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveGoal(index, -1)} disabled={index === 0} className="p-0.5 text-secondary hover:text-primary disabled:opacity-20" data-testid={`goal-up-${goal.id}`}>
                      <ChevronUp size={14} />
                    </button>
                    <button onClick={() => moveGoal(index, 1)} disabled={index === sortedGoals.length - 1} className="p-0.5 text-secondary hover:text-primary disabled:opacity-20" data-testid={`goal-down-${goal.id}`}>
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-body text-sm ${goal.enabled ? 'text-primary' : 'text-secondary line-through'}`}>
                      {cap(goal.text)}
                    </p>
                    {goal.easyText && (
                      <p className="font-body text-xs text-secondary truncate">Easy: {goal.easyText}</p>
                    )}
                  </div>
                  <Switch checked={goal.enabled} onCheckedChange={(v) => handleToggleGoal(goal.id, v)} data-testid={`goal-toggle-${goal.id}`} />
                  <button onClick={() => handleEditGoal(goal)} className="p-1.5 rounded-full hover:bg-theme-secondary" data-testid={`goal-edit-${goal.id}`}>
                    <Edit3 size={14} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
                  </button>
                  <button onClick={() => handleDeleteGoal(goal.id)} className="p-1.5 rounded-full hover:bg-theme-secondary" data-testid={`goal-delete-${goal.id}`}>
                    <Trash2 size={14} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Your daily reflections */}
<motion.div
  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.13 }}
  className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
>
  <div className="flex items-center justify-between mb-4">
    <h2 className="font-body text-primary text-center flex-1" style={{ fontWeight: 600 }}>Daily Reflections:</h2>
    <div className="flex items-center gap-1">
      {(settings.prompts || []).length < 10 && (
        <button onClick={handleAddPrompt} className="p-2 rounded-full hover:bg-theme-light transition-colors" data-testid="add-prompt-btn">
          <Plus size={20} style={{ color: 'var(--theme-accent, #8E6ECC)' }} />
        </button>
      )}
      {/* DELETE THIS ENTIRE BUTTON */}
      {/* 
      <button onClick={handleRestoreAll} className="p-2 rounded-full hover:bg-theme-light transition-colors" data-testid="restore-defaults-btn" title="Restore defaults">
        <Plus size={20} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
      </button>
      */}
    </div>
  </div>

  <div className="space-y-2">
    {sortedPrompts.map((prompt, index) => (
      <div key={prompt.id} className="p-3 rounded-xl bg-theme-light flex items-center gap-2" data-testid={`prompt-item-${prompt.id}`}>
        <div className="flex flex-col gap-0.5">
          <button onClick={() => movePrompt(index, -1)} disabled={index === 0} className="p-0.5 text-secondary hover:text-primary disabled:opacity-20" data-testid={`prompt-up-${prompt.id}`}>
            <ChevronUp size={14} />
          </button>
          <button onClick={() => movePrompt(index, 1)} disabled={index === sortedPrompts.length - 1} className="p-0.5 text-secondary hover:text-primary disabled:opacity-20" data-testid={`prompt-down-${prompt.id}`}>
            <ChevronDown size={14} />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-body text-sm ${prompt.enabled ? 'text-primary' : 'text-secondary line-through'}`}>
            {prompt.question}
          </p>
          {prompt.isDefault && (
            <span className="font-body text-[10px] text-theme-accent">Default</span>
          )}
        </div>
        <button onClick={() => handleTogglePrompt(prompt.id, !prompt.enabled)} className="p-1.5 rounded-full hover:bg-theme-secondary" data-testid={`prompt-toggle-${prompt.id}`}>
          {prompt.enabled ? <Eye size={14} style={{ color: 'var(--theme-accent, #A58CD6)' }} /> : <EyeOff size={14} className="text-secondary" />}
        </button>
        <button onClick={() => handleEditPrompt(prompt)} className="p-1.5 rounded-full hover:bg-theme-secondary" data-testid={`prompt-edit-${prompt.id}`}>
          <Edit3 size={14} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
        </button>
        {prompt.isDefault ? (
          prompt.question !== prompt.defaultQuestion && (
            <button onClick={() => handleResetPrompt(prompt.id)} className="p-1.5 rounded-full hover:bg-theme-secondary" data-testid={`prompt-reset-${prompt.id}`} title="Reset to original">
              <RotateCcw size={14} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
            </button>
          )
        ) : (
          <button onClick={() => handleDeletePrompt(prompt.id)} className="p-1.5 rounded-full hover:bg-theme-secondary" data-testid={`prompt-delete-${prompt.id}`}>
            <Trash2 size={14} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
          </button>
        )}
      </div>
    ))}
  </div>
</motion.div>

        {/* Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.17 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <div className="space-y-4">
            <div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'small', label: 'Small Text' },
                  { key: 'xlarge', label: 'Large Text' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => handleTextScaleChange(key)}
                    className={`rounded-xl px-3 py-2 text-sm font-body transition-colors border ${((textScale === 'xlarge' ? 'xlarge' : 'small') === key) ? 'bg-theme-secondary border-theme-primary text-primary' : 'bg-theme-light border-theme-secondary text-secondary'}`}
                    data-testid={`text-size-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(themes).map(([key, themeData]) => (
                  <button key={key} onClick={() => changeTheme(key)}
                    className={`relative p-4 rounded-xl border-2 transition-all ${theme === key ? 'border-theme-accent' : 'border-theme-secondary hover:border-theme-primary'}`}
                    style={{ backgroundColor: themeData.light }}
                    data-testid={`theme-${key}`}
                  >
                    <div className="w-full h-8 rounded-lg mb-2" style={{ backgroundColor: themeData.primary }} />
                    <span
                      className="font-body text-xs theme-swatch-label"
                      style={{ '--swatch-label-color': getContrastTextColor(themeData.light) }}
                    >
                      {themeData.name}
                    </span>
                    {theme === key && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: themeData.accent }}>
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {(aiSupported || isNativeiOS()) && (
              <div className="pt-2">
                <div className="flex items-center justify-between">
                  <p className="font-body text-sm text-primary">On-device AI Companion</p>
                  <Switch checked={aiCompanionEnabled} onCheckedChange={handleAICompanionToggle} data-testid="ai-companion-toggle" />
                </div>
              </div>
            )}

            <div className="pt-2">
              <div className="flex items-center justify-between py-1">
                <p className="font-body text-sm text-primary">Set Passcode</p>
                <Switch checked={securityType === 'passcode'} onCheckedChange={handlePasscodeToggle} />
              </div>

              {biometricsAvailable && (
                <div className="flex items-center justify-between py-1 mt-2">
                  <p className="font-body text-sm text-primary">Face ID / Touch ID</p>
                  <Switch checked={securityType === 'biometric'} onCheckedChange={handleBiometricsToggle} />
                </div>
              )}
            </div>

            <div className="pt-2">
              <div className="flex items-center justify-between">
                <p className="font-body text-sm text-primary">Daily Reminder</p>
                <Switch checked={notifSettings.enabled} onCheckedChange={handleNotificationToggle} disabled={!notifSupported} data-testid="notification-toggle" />
              </div>
              {notifSettings.enabled && (
                <div className="mt-3 flex justify-end">
                  <input type="time" value={notifSettings.time} onChange={handleTimeChange} className="time-input-theme bg-theme-light border border-theme-secondary rounded-xl px-3 py-2 font-body text-primary" data-testid="notification-time" />
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Help */}
        <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          onClick={() => navigate('/help')}
          className="w-full bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4 text-left transition-all hover:shadow-interactive"
          data-testid="help-btn"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-body text-primary">Help & Instructions</h2>
            <ChevronRight size={18} style={{ color: 'var(--theme-accent, #A58CD6)' }} />
          </div>
        </motion.button>

        {/* Privacy */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="bg-theme-light rounded-3xl p-6 shadow-ambient border border-theme-secondary mb-4"
        >
          <div>
            <h2 className="font-body text-primary mb-2">Your Data Is Yours</h2>
            <p className="font-body text-sm text-secondary leading-relaxed mb-3">
              All data stays on this device. If you delete the app, it is wiped. Export to keep a copy.
            </p>
            <a href="https://www.re-build-me.com/p/privacy-policy-for-re-build-me.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-body text-sm text-theme-accent hover:text-theme-dark transition-colors" data-testid="privacy-policy-link">
              Privacy Policy <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>

        {/* Blog Link - Styled Box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.01 }}
          className="bg-theme-light rounded-3xl p-6 border border-theme-secondary mb-4"
          data-testid="blog-section"
        >
          <div>
            <p className="font-body text-sm text-secondary leading-relaxed mb-4">
              Learn more about the rebuilding cycle:
              <br /><br />
              Action creates evidence.
              <br />
              Evidence builds belief.
              <br />
              Belief becomes identity.
              <br /><br />
              Created by someone who is also rebuilding himself.
              <br /><br />
              All the best,<br />
              Justin Tee
            </p>

            {/* Blog Link - Matching Home screen button style */}
            <a
              href="https://www.re-build-me.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary mb-4"
              data-testid="blog-link"
            >
              Read My Story
            </a>

            {/* Contact section */}
            <div className="pt-3">
              <p className="font-body text-sm text-secondary leading-relaxed mb-3">
                Have feedback or just want to say hello?
              </p>

              {/* Email Link - Matching Home screen button style */}
              <a
                href="mailto:mail@re-build-me.com"
                className="inline-flex w-56 items-center justify-center gap-2 rounded-full px-8 py-4 font-body font-medium transition-all hover:bg-theme-hover hover:-translate-y-0.5 shadow-sm bg-theme-primary text-primary"
                data-testid="email-link"
              >
                Email Me
                <Mail size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Version */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-center py-4">
          <p className="font-body text-xs text-secondary/60">RE-BUILD-ME v1.6</p>
        </motion.div>
      </div>

      {/* Passcode Dialog */}
      <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
        <DialogContent className="bg-theme-light rounded-3xl max-w-xs mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">{passcodeStep === 'enter' ? 'Set Passcode' : 'Confirm Passcode'}</DialogTitle>
            <DialogDescription className="text-center font-body text-secondary">
              {passcodeStep === 'enter' ? 'Enter a 4-digit passcode' : 'Re-enter your passcode to confirm'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-6 py-4">
  <div className="flex justify-center gap-3">
    {[0, 1, 2, 3].map((i) => (
      <input
        key={i}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={1}
        value={(passcodeStep === 'enter' ? passcodeValue : confirmPasscode)[i] || ''}
        onChange={(e) => {
          const newValue = e.target.value;
          if (newValue && !/^\d$/.test(newValue)) return;
          
          const currentValue = passcodeStep === 'enter' ? passcodeValue : confirmPasscode;
          const newCode = currentValue.split('');
          newCode[i] = newValue || '';
          const newCodeString = newCode.join('');
          
          if (passcodeStep === 'enter') {
            setPasscodeValue(newCodeString);
            // Auto-focus next input
            if (newValue && i < 3) {
              document.getElementById(`passcode-input-${i + 1}`)?.focus();
            }
          } else {
            setConfirmPasscode(newCodeString);
            if (newValue && i < 3) {
              document.getElementById(`confirm-passcode-input-${i + 1}`)?.focus();
            }
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Backspace') {
            const currentValue = passcodeStep === 'enter' ? passcodeValue : confirmPasscode;
            if (!currentValue[i] && i > 0) {
              document.getElementById(passcodeStep === 'enter' ? `passcode-input-${i - 1}` : `confirm-passcode-input-${i - 1}`)?.focus();
            }
          }
        }}
        id={passcodeStep === 'enter' ? `passcode-input-${i}` : `confirm-passcode-input-${i}`}
        className="w-14 h-14 text-center text-xl font-bold rounded-xl border-2 border-theme-primary bg-theme-light focus:border-theme-accent focus:outline-none"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        data-testid={`passcode-input-${i}`}
      />
    ))}
  </div>
  
  <Button 
    onClick={handlePasscodeSubmit} 
    disabled={(passcodeStep === 'enter' ? passcodeValue : confirmPasscode).length !== 4} 
    className="w-full bg-theme-primary text-primary hover:bg-theme-hover rounded-full" 
    data-testid="passcode-submit"
  >
    {passcodeStep === 'enter' ? 'Continue' : 'Set Passcode'}
  </Button>
</div>
        </DialogContent>
      </Dialog>

      {/* Goal Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="bg-theme-light rounded-3xl max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editingGoal ? 'Edit Goal' : 'Add Goal'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block font-body text-sm text-secondary mb-2">Your goal</label>
              <Input value={goalForm.text} onChange={(e) => setGoalForm({ ...goalForm, text: e.target.value })} placeholder="eg. Go for a 20 minute walk" className="bg-theme-light border-theme-secondary rounded-xl" data-testid="goal-text-input" />
            </div>
            <div>
              <label className="block font-body text-sm text-secondary mb-2">
                Your easy goal (optional)
              </label>
              <Input value={goalForm.easyText} onChange={(e) => setGoalForm({ ...goalForm, easyText: e.target.value })} placeholder="eg. Sit in the Garden" className="bg-theme-light border-theme-secondary rounded-xl" data-testid="goal-easy-input" />
            </div>
            <Button onClick={handleSaveGoal} className="w-full bg-theme-primary text-primary hover:bg-theme-hover rounded-full" data-testid="goal-save-btn">
              {editingGoal ? 'Save Changes' : 'Add Goal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prompt Dialog */}
      <Dialog open={showPromptDialog} onOpenChange={setShowPromptDialog}>
        <DialogContent className="bg-theme-light rounded-3xl max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editingPrompt ? 'Edit Reflection' : 'Add Reflection'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block font-body text-sm text-secondary mb-2">Question text (max 150 characters)</label>
              <Input value={promptForm.question} onChange={(e) => setPromptForm({ question: e.target.value.slice(0, 150) })} placeholder="e.g., What am I grateful for?" className="bg-theme-light border-theme-secondary rounded-xl" data-testid="prompt-question-input" />
              <span className="text-xs text-secondary mt-1 block">{promptForm.question.length}/150</span>
            </div>
            <Button onClick={handleSavePrompt} className="w-full bg-theme-primary text-primary hover:bg-theme-hover rounded-full" data-testid="prompt-save-btn">
              {editingPrompt ? 'Save Changes' : 'Add Reflection'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );
};

export default Settings;